/**
 * TV 리모컨 포커스 — **이 폴더에서 유일하게 DOM 을 만지는 곳** (v10, `docs/25` §4).
 *
 * 판정은 전부 `keys`·`geometry`·`decide` 가 하고 여기는 **읽고 실행할 뿐이다.**
 * `eslint.config.mjs` 의 `no-restricted-globals` 가 이 파일 하나만 예외로 둔다 —
 * `detectClientTypeFromWindow()` 와 같은 지위이고, 그래서 **테스트하지 않는다.**
 *
 * ── 리스너가 `App` 최상위에 있는 이유 (절대 규칙 27) ──────────
 *
 * 층을 React 상태가 아니라 **DOM(`[data-focus-scope]`)에서 읽으므로** 이 훅은 시청 화면의
 * 상태를 하나도 알 필요가 없다. 그래서 `ChannelGate` **위**에 설 수 있고, 채널 전환
 * (`WatchScreen key={channelId}`)에 언마운트되지 않는다 — **누수할 자리 자체가 없다.**
 * 「정리를 잘 쓰자」가 아니라 구조가 그렇다.
 *
 * 핸들러가 **클로저에 아무것도 담지 않는 것**이 그것을 떠받친다. 호출 시점에 DOM 을 읽을
 * 뿐이라 모듈 스코프 함수 하나로 족하고, 그래서 등록·해제가 같은 참조이며 의존성 배열이
 * 정직하다. 상태가 있는 설계였다면 배열이 커지고, 그것이 재등록을 부르고, 재등록마다
 * 정리를 빠뜨릴 자리가 생긴다.
 */

import { useEffect, useState } from 'react';

import {
  isRemoteControlled,
  readAppDisplay,
  resolveClientType,
  usesKeyboardNav,
} from '../client-type.js';
import { decideNavAction } from './decide.js';
import { entryFocusIndex } from './geometry.js';
import type { FocusCandidate } from './geometry.js';
import { resolveNavIntent } from './keys.js';

/**
 * 포커스 후보.
 *
 * **`iframe` 이 빠지는 것이 의도다.** GIS 로그인 버튼은 교차 출처라 포커스가 들어가면
 * 우리 `keydown` 이 보이지 않고 **리모컨이 영영 갇힌다.** 빼는 대가는 TV 모드로 로그인할
 * 수 없다는 것이고, `Tab` 을 가로채지 않으므로 키보드가 있으면 거기 닿을 수 있다
 * (`docs/25` §9).
 *
 * `<video>` 도 자동으로 빠진다 — 어댑터가 만드는 엘리먼트에 `controls` 도 `tabindex` 도
 * 없다 (절대 규칙 31).
 */
const FOCUSABLE = 'button:not([disabled]), a[href]';

/**
 * 지금 층의 뿌리. **DOM 순서의 마지막 `[data-focus-scope]`** 이고, 없으면 문서 전체다.
 *
 * 셀렉터 목록을 여기 두지 않는 것이 요점이다 — 표시는 **렌더하는 자리**에 있고
 * (절대 규칙 29), 그래야 화면이 바뀔 때 목록만 조용히 낡는 일이 없다.
 */
function scopeRoot(): HTMLElement {
  const scopes = document.querySelectorAll<HTMLElement>('[data-focus-scope]');
  return scopes.length === 0 ? document.body : (scopes[scopes.length - 1] ?? document.body);
}

/**
 * 그려진 후보를 DOM 순서로 모은다. **매 키마다 새로 뽑는다 — 캐시하지 않는다.**
 *
 * `skip` 버튼은 뜨고 지고 `purchase` 는 요청 중에 `disabled` 가 된다. 캐싱하면 사라진
 * 노드를 가리켜 **한 번 아무 데도 가지 않는다.**
 *
 * `getClientRects().length === 0` 은 「실제로 그려지지 않았다」다 —
 * `display:none` 인 조상 아래 있는 것이 그렇게 걸린다.
 */
function collect(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.getClientRects().length > 0,
  );
}

function toCandidate(el: HTMLElement, index: number): FocusCandidate {
  const { left, top, right, bottom } = el.getBoundingClientRect();
  return { index, rect: { left, top, right, bottom } };
}

/**
 * 글자를 받는 자리인가.
 *
 * **지금 이 앱에는 하나도 없다** — 그래서 이 함수가 거짓을 돌려주지 않아도 증상이 없고,
 * 그것이 정확히 조용한 결함의 모양이다. 훗날 검색창이 생기는 날 화살표로 커서를 못
 * 움직이는 것을 여기서 막는다.
 */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * 키 하나를 처리한다. **모듈 스코프 함수다** — 위 주석의 「클로저에 아무것도 담지 않는다」.
 *
 * `preventDefault` 를 **스스로 판단하지 않는다.** `decideNavAction` 이 반환값으로 내고
 * 여기는 그대로 실행할 뿐이다 (절대 규칙 31) — 어댑터가 판단하면 그것이 검사 밖으로 나간다.
 */
function handleKeyDown(event: KeyboardEvent): void {
  const intent = resolveNavIntent({
    key: event.key,
    // 구형 TV 브라우저의 폴백. 표준에서 폐기됐지만 그 기기들이 주는 유일한 값이다.
    keyCode: event.keyCode,
    repeat: event.repeat,
    alt: event.altKey,
    ctrl: event.ctrlKey,
    meta: event.metaKey,
    shift: event.shiftKey,
    editable: isEditable(event.target),
  });
  if (intent === null) return;

  const elements = collect(scopeRoot());
  const activeIndex = elements.findIndex((el) => el === document.activeElement);
  const backIndex = elements.findIndex((el) => el.dataset.focusBack !== undefined);

  const action = decideNavAction({
    intent,
    candidates: elements.map(toCandidate),
    // **`-1` 은 「층 밖」이다.** 시트를 연 버튼·언마운트된 버튼·iframe 이 전부 여기 걸리고,
    // `decide` 가 그것을 삼켜 층 안으로 들여놓는다 (절대 규칙 32).
    activeIndex: activeIndex < 0 ? null : activeIndex,
    backIndex: backIndex < 0 ? null : backIndex,
  });

  if (action.preventDefault) event.preventDefault();

  if (action.kind === 'focus') {
    // 기본 동작인 스크롤을 막지 않는다 — 긴 시트의 아래쪽에 닿는 유일한 수단이다.
    elements[action.index]?.focus();
  } else if (action.kind === 'activate') {
    // **`click()` 을 부르는 유일한 자리다** (절대 규칙 30 의 예외). 뒤로 키는 네이티브
    // 활성화가 없으므로 우리가 누르는 것 말고 방법이 없다.
    elements[action.index]?.click();
    healAfterClose();
  }
}

/**
 * 층을 닫은 **다음 프레임**에 포커스를 되돌린다 (`docs/25` §6-4).
 *
 * ── 왜 필요한가 ───────────────────────────────────────────────
 *
 * 뒤로 키가 닫기 버튼을 누르면 React 가 그 시트를 언마운트하고, **포커스한 버튼이 사라져
 * 브라우저가 포커스를 `<body>` 로 떨어뜨린다.** 다음 키가 스스로 회복하지만(`decide` 의
 * 층 밖 분기) 그 사이에 **링이 사라진다** — 3m 떨어진 TV 앞에서 그것은 「리모컨이
 * 죽었다」와 구분되지 않는다. K4 실기에서 실제로 그렇게 보였다.
 *
 * ── 왜 상태가 아닌가 ──────────────────────────────────────────
 *
 * 「시트를 연 버튼을 기억했다가 돌려준다」가 자연스러워 보이지만 그것은 상태이고, 상태는
 * 화면과 어긋날 수 있다(그 버튼도 이미 사라졌을 수 있다). 여기서는 **키를 누를 때와 똑같은
 * 규칙**을 한 번 더 돌릴 뿐이다 — 「활성 요소가 지금 층의 후보가 아니면 첫 후보로」.
 *
 * `requestAnimationFrame` 인 이유는 React 의 재렌더가 `click()` 이 돌아온 시점에 끝났다고
 * **보장되지 않기** 때문이다. 아직이면 `scopeRoot()` 가 사라질 시트를 가리켜 그 안으로
 * 포커스를 넣고, 결과는 다시 `<body>` 다.
 *
 * ★ **v21 이 이것을 export 한다. 새로 쓰지 않는다.** 광고가 열리고 닫힐 때 컨트롤 바의
 * 버튼 구성이 통째로 갈리는데(「전체 회차」·회차 스킵이 사라지고 광고 구매·광고 스킵이
 * 생긴다), 포커스가 그 사라진 버튼에 있었으면 **`<body>` 로 떨어진다** — 그 상태에서는
 * 방향키가 아무 데도 가지 않아 「리모컨이 죽었다」로 보인다 (절대 규칙 32).
 */
export function healAfterClose(): void {
  requestAnimationFrame(() => {
    const elements = collect(scopeRoot());
    if (elements.some((el) => el === document.activeElement)) return;
    const entry = entryFocusIndex(elements.map(toCandidate));
    if (entry !== null) elements[entry]?.focus();
  });
}

/**
 * `App()` 최상위에서 한 번 부른다.
 *
 * **켜지는 곳이 둘이다** — TV 계열(`docs/25` §6)과 **설치된 데스크톱 앱**(v19).
 * 브라우저 탭의 PC·모바일에서는 리스너조차 붙지 않으므로 그 경로는 바이트 단위로 그대로다.
 *
 * **둘을 `data-tv-focus` 의 값으로 가른다.**
 *
 * | 값 | 어디 | 링 |
 * |---|---|---|
 * | `on` | TV 계열 | `:focus` — 마우스가 없으니 **결정적**이어야 한다 |
 * | `hybrid` | 설치된 데스크톱 앱 | `:focus-visible` — **마우스가 있다.** 눌렀던 버튼마다 링이 남으면 안 된다 |
 * | (없음) | 브라우저 탭 | 키 조작 자체가 꺼짐 |
 *
 * **한 속성으로 두는 이유**는 하네스가 「기능이 통째로 꺼져 있다」를 신고하는 축이
 * 이것 하나이기 때문이다(아래 주석). 값으로 갈라야 `styles.css` 의 두 규칙이 겹치지
 * 않는다(절대 규칙 35).
 *
 * `resolveClientType()` 를 **`WatchScreen` 과 두 곳에서** 고정한다. 같은 쿼리를 읽으므로
 * 값이 갈릴 일은 사실상 없고(다만 `detectClientType` 이 `window.innerWidth` 를 보므로
 * 부팅 중에 창 크기가 크게 바뀌면 이론상 갈린다), **prop 을 `App → AuthGate → Shell →
 * ChannelGate → WatchScreen` 5단계로 꿰지 않는 것**이 이 설계의 요점이다.
 *
 * `capture: true` — React 의 위임(루트 컨테이너)보다 먼저 본다.
 */
export function useTvFocus(): void {
  const [mode] = useState<'on' | 'hybrid' | null>(() => {
    const clientType = resolveClientType();
    if (isRemoteControlled(clientType)) return 'on';
    return usesKeyboardNav(clientType, readAppDisplay()) ? 'hybrid' : null;
  });

  useEffect(() => {
    if (mode === null) return;

    // 포커스 링의 스코프이자, **하네스가 「기능이 통째로 꺼져 있다」를 신고할 수 있는
    // 유일한 축이다** — 없으면 아무도 그것을 알아차리지 못하고 데모 당일에 안다.
    document.documentElement.dataset.tvFocus = mode;
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      delete document.documentElement.dataset.tvFocus;
    };
  }, [mode]);
}
